import React, { useState } from 'react';
import axios from 'axios';
import { ArrowRight, CheckCircle2, Loader2, Phone, Mail, MapPin } from 'lucide-react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import { Toaster } from './ui/toaster';
import { company } from '../mock/mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomeContactForm = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', agency: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast({ title: 'Almost there', description: 'Please add your name and email so we can reply.' });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, { ...form, service: '', branches: '' }, { timeout: 20000 });
      setDone(true);
      toast({ title: 'Thanks — we have you', description: 'A senior account manager will be in touch shortly.' });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Something went wrong. Please call us on 03333 445 336.';
      toast({ title: 'Could not send enquiry', description: detail });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="home-contact" className="sect bg-[var(--rs-cream)]">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.2em] text-[var(--rs-green)] mb-3">Get in touch</div>
            <h2 className="h-display text-4xl md:text-5xl text-[var(--rs-ink)] leading-tight">
              Tell us about your branch — we will reply within the hour.
            </h2>
            <p className="mt-5 text-[var(--rs-ink)]/70 text-lg max-w-md">
              Whether you have one branch or fifty, our team will scope a setup that fits. No obligation, no pressure.
            </p>

            <div className="mt-8 space-y-4 text-sm">
              <a href={`tel:${company.phone}`} className="flex items-center gap-3 group">
                <span className="w-10 h-10 rounded-lg bg-white border border-[#E4DFF7] text-[var(--rs-green)] grid place-items-center"><Phone className="w-4 h-4" /></span>
                <span className="text-[var(--rs-ink)] group-hover:text-[var(--rs-coral)]">{company.phone}</span>
              </a>
              <a href={`mailto:${company.email}`} className="flex items-center gap-3 group">
                <span className="w-10 h-10 rounded-lg bg-white border border-[#E4DFF7] text-[var(--rs-green)] grid place-items-center"><Mail className="w-4 h-4" /></span>
                <span className="text-[var(--rs-ink)] group-hover:text-[var(--rs-coral)]">{company.email}</span>
              </a>
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-lg bg-white border border-[#E4DFF7] text-[var(--rs-green)] grid place-items-center"><MapPin className="w-4 h-4" /></span>
                <span className="text-[var(--rs-ink)]/75">{company.address}</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="rs-card p-7 md:p-9">
              {done ? (
                <div className="text-center py-10">
                  <div className="w-14 h-14 mx-auto rounded-full bg-[var(--rs-green)]/10 text-[var(--rs-green)] grid place-items-center"><CheckCircle2 className="w-7 h-7" /></div>
                  <h3 className="h-display text-2xl mt-4 text-[var(--rs-ink)]">Thanks {form.name.split(' ')[0]} — we have you.</h3>
                  <p className="text-[var(--rs-ink)]/65 mt-2 max-w-md mx-auto">A senior account manager will be in touch shortly.</p>
                  <button onClick={() => { setDone(false); setForm({ name: '', agency: '', email: '', phone: '', message: '' }); }} className="btn-ghost mt-6">Send another</button>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[var(--rs-ink)]">Full name *</Label>
                    <Input value={form.name} onChange={handle('name')} placeholder="Your full name" className="mt-1.5 bg-white border-[#E4DFF7] focus-visible:ring-[var(--rs-green2)]" />
                  </div>
                  <div>
                    <Label className="text-[var(--rs-ink)]">Agency name</Label>
                    <Input value={form.agency} onChange={handle('agency')} placeholder="Your agency" className="mt-1.5 bg-white border-[#E4DFF7]" />
                  </div>
                  <div>
                    <Label className="text-[var(--rs-ink)]">Work email *</Label>
                    <Input type="email" value={form.email} onChange={handle('email')} placeholder="you@agency.co.uk" className="mt-1.5 bg-white border-[#E4DFF7]" />
                  </div>
                  <div>
                    <Label className="text-[var(--rs-ink)]">Phone</Label>
                    <Input value={form.phone} onChange={handle('phone')} placeholder="Best contact number" className="mt-1.5 bg-white border-[#E4DFF7]" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-[var(--rs-ink)]">How can we help?</Label>
                    <Textarea rows={4} value={form.message} onChange={handle('message')} placeholder="Tell us a little about what you need…" className="mt-1.5 bg-white border-[#E4DFF7]" />
                  </div>
                  <div className="md:col-span-2 flex flex-col md:flex-row md:items-center md:justify-between gap-3 pt-1">
                    <p className="text-xs text-[var(--rs-ink)]/55 max-w-sm">By submitting you agree to our privacy policy. We never share your details with third parties.</p>
                    <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-70 disabled:cursor-not-allowed">
                      {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>) : (<>Send enquiry <ArrowRight className="w-4 h-4" /></>)}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
      <Toaster />
    </section>
  );
};

export default HomeContactForm;
